import { supabase } from './client';
import { error as logError } from '../../utils/logger';

/**
 * Koppelcode-acties voor de leerling zelf.
 *
 * ─── WAAROM DIT BESTAAT ──────────────────────────────────────────────────────
 *
 * `regenerate_link_code()` staat sinds migratie 0013 in de database en is open
 * voor elke ingelogde leerling — maar er was geen enkele knop die hem aanriep.
 * Vervangen kon alléén een beheerder, via break glass.
 *
 * Dat is de verkeerde verdeling. Deelt een kind zijn code te breed (in een
 * klasgroep, met een vriendje, per ongeluk in beeld tijdens een videogesprek),
 * dan is dat kind degene die het weet en degene die er last van heeft. Moet het
 * daarvoor support benaderen, dan gebeurt het niet en blijft de code permanent
 * geldig.
 *
 * Zie `docs/project/SHORT_CODE_SECURITY.md` — intrekbaarheid weegt in dit
 * domein zwaarder dan een korte levensduur, omdat de aannemelijke lekpaden
 * (kind deelt de code, scherm blijft open) door geen enkele TTL geraakt worden.
 */

export interface RegenerateLinkCodeResult {
  ok: boolean;
  /** De nieuwe code zoals de SERVER hem kent. */
  code?: string;
  message?: string;
}

/**
 * Vervangt de koppelcode van de ingelogde leerling.
 *
 * Geeft de code terug die de server heeft weggeschreven, niet een lokaal
 * verzonnen waarde. Dat verschil was eerder een echte bug: het registratiescherm
 * toonde een zelfbedachte code terwijl de database een andere aanmaakte, en het
 * kind schreef een code op die bij geen enkel profiel hoorde.
 */
export async function regenerateOwnLinkCode(): Promise<RegenerateLinkCodeResult> {
  const {
    data,
    error
  } = await supabase.rpc('regenerate_link_code');
  if (error) {
    logError('[LinkCode] Nieuwe koppelcode mislukt', error);
    return {
      ok: false,
      message: describeError(error.message)
    };
  }
  return {
    ok: true,
    code: String(data)
  };
}

/**
 * Haalt de koppelcode op zoals de SERVER hem nu kent.
 *
 * ─── WAAROM DIT NODIG IS ─────────────────────────────────────────────────────
 *
 * De instellingen en de profielkaart toonden `learnerAccount.linkCode` uit de
 * lokale opslag. Die waarde is een momentopname van het laatste moment dat dit
 * apparaat iets schreef — en er zijn twee manieren waarop de server hem daarna
 * kan wijzigen zonder dat dit apparaat het merkt:
 *
 *   1. een beheerder vervangt de code via break glass (`admin_regenerate_link_code`);
 *   2. het kind vervangt hem op een ánder apparaat.
 *
 * In beide gevallen leest het kind een code voor die nergens meer bij hoort, en
 * de ouder krijgt "code niet gevonden". Precies dezelfde faalvorm als de bug in
 * het registratiescherm: het scherm toont met stelligheid iets wat niet bestaat,
 * en de fout komt pas dagen later bij iemand anders naar boven.
 *
 * Bij een fout geven we bewust GEEN code terug in plaats van een oude: liever
 * niets tonen dan het verkeerde. De aanroeper houdt dan zijn huidige waarde.
 */
export async function fetchOwnLinkCode(learnerId: string): Promise<string | null> {
  const {
    data,
    error
  } = await supabase.from('profiles').select('link_code').eq('id', learnerId).maybeSingle();
  if (error) {
    logError('[LinkCode] Koppelcode ophalen mislukt', error);
    return null;
  }
  const code = data?.link_code;
  return typeof code === 'string' && code.length > 0 ? code : null;
}

/**
 * Vertaalt de databasefout naar iets waar een kind van tien iets aan heeft.
 *
 * Alleen de begrenzing krijgt een eigen tekst: dat is de enige uitkomst die de
 * leerling zelf kan oplossen (wachten). Al het andere is een storing, en dan is
 * de ruwe Postgres-melding onbruikbaar — die noemt interne functienamen en zegt
 * niets over wat er nu te doen valt.
 *
 * De geruststelling "je oude code werkt gewoon nog" staat er bewust bij: de
 * functie begrenst vóórdat ze iets wijzigt, dus dat is waar. Zonder die zin
 * moet het kind raden of het nu zonder code zit.
 */
function describeError(raw: string | undefined): string {
  if (raw && raw.includes('over een uur')) {
    return 'Je hebt net al een paar keer een nieuwe code gemaakt. Probeer het over een uur nog eens. Je oude code werkt gewoon nog.';
  }
  return 'Er ging iets mis. Je oude code werkt gewoon nog — probeer het straks opnieuw.';
}
